import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Callout, ChoiceMenu, EndLine, ListRow, Loading, Page, PageHeader, Section, Waiting, useRowKeys } from '@beyond-js/ui/react';
import { Comparison, Describe, type Capture, type FileChange, type PackageChange } from '@snapshots/domain';
import { Address, Origin, useFrame, usePlace } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Failure, Missing, OutageScope, Outage, Outages, Problems, Unanswered, useMissing, useOutage, type Missed } from '@snapshots/ui';

const states = { added: 'success', removed: 'stopped', version: 'pending', content: 'attention', same: 'idle' } as const;

type Side = 'before' | 'after';

/**
 * Two snapshots side by side (SNP-10): `?a=` before and `?b=` after. Each package is said added, removed,
 * changed version, changed content (another digest under the same version) or the same; a package that
 * changed opens the files that changed in it, from the two archives' listings by digest. Two snapshots of
 * the same content say so in one line. "After" can be changed to any other snapshot of the same project.
 * Another pair is another comparison: what was read for one is never shown for the next.
 */
export function Compare() {
	const [parameters] = useSearchParams();
	const a = parameters.get('a') ?? '';
	const b = parameters.get('b') ?? '';
	return <Comparing key={`${a}|${b}`} a={a} b={b} />;
}

/**
 * Each snapshot is its own read. One nobody answered is said once (D163): the comparison needs both, so with
 * either unanswered the page's one line is its body, and Try again reads again only what did not answer.
 * The files of a package nobody answered say "Not available right now" in their place, under the same line.
 */
function Comparing({ a, b }: { a: string; b: string }) {
	const [, setParameters] = useSearchParams();
	const { text, captures, language, short } = usePresence();
	const { arrival } = useFrame();
	const [read, setRead] = useState<Partial<Record<Side, Capture>>>({});
	const [missed, setMissed] = useState<Partial<Record<Side, Refusal>>>({});
	const [refused, setRefused] = useState<Refusal>();
	const [others, setOthers] = useState<Capture[]>([]);
	const missing = useMemo(() => new Missing(), []);
	const regional = useMissing(missing);
	const live = useRef(true);
	const pair: [Capture, Capture] | undefined = read.before && read.after ? [read.before, read.after] : undefined;
	usePlace(pair ? { project: pair[1].project, organization: pair[1].organization } : refused || missed.before || missed.after ? {} : undefined);
	useEffect(() => {
		document.title = text('page.compare');
	}, [text]);
	// One read of one side, resolving whether it was answered
	const load = useCallback(
		async (side: Side): Promise<boolean> => {
			try {
				const found = await captures.get(side === 'before' ? a : b);
				if (!live.current) return true;
				setRead(current => ({ ...current, [side]: found }));
				setMissed(current => ({ ...current, [side]: undefined }));
				return true;
			} catch (cause) {
				const refusal = Problems.refusal(cause);
				if (!live.current || refusal.quiet) return true;
				if (Outage.cause(refusal)) {
					setMissed(current => ({ ...current, [side]: refusal }));
					return false;
				}
				// A refusal is an answer: it ends the line
				setMissed(current => ({ ...current, [side]: undefined }));
				setRefused(refusal);
				return true;
			}
		},
		[captures, a, b]
	);
	useEffect(() => {
		live.current = true;
		void load('before');
		void load('after');
		return () => {
			live.current = false;
		};
	}, [load]);
	// The other snapshots of the project, for "After"
	const project = pair?.[1].project;
	useEffect(() => {
		if (!project) return;
		void captures.find({ project, state: 'active', limit: 50 }).then(list => live.current && setOthers(list.captures), () => undefined);
	}, [captures, project]);

	const sides = (['before', 'after'] as const).flatMap(side => {
		const refusal = missed[side];
		return refusal ? [{ key: side, title: text(`outage.read.${side}`), refusal, run: () => load(side) }] : [];
	});
	// A snapshot that cannot be read is an answer: nothing can be compared, so nothing is read again
	const reads: Missed[] = refused ? [] : [...sides, ...regional];
	// The one line keeps one place, at the column's start, whether it is the page's body or a line above it
	const page = (children: ReactNode, facts?: ReactNode) => (
		<Page template="detail" width="standard" arrival={<Origin project={pair?.[1].project} organization={pair?.[1].organization} waiting={!pair && !refused && !sides.length} />} header={<PageHeader title={text('compare.title')} headingRef={arrival.heading} crumbs={[{ label: text('nav.captures'), href: pair ? Address.of({ project: pair[1].project }) : '/' }]} facts={facts} />}>
			<Outages reads={reads} whole={!pair} text={text} language={language} />
			{children}
		</Page>
	);

	if (refused) return page(refused.code === 'NOT_FOUND' ? <Callout tone="warning" title={text('compare.missing')} /> : <Failure refusal={refused} text={text} locale={language} attempt="read" />);
	// Nothing to compare until both answer: the line is the page's body
	if (!pair && sides.length) return page(null);
	// The packages wait in their own section, under the header already drawn (LOAD-01)
	if (!pair) return page(<Section title={text('capture.packages')} waiting={{ label: Loading.text({ name: text('loading.compare'), language }), language, rows: 3 }} />);
	const [before, after] = pair;
	const changes = Comparison.packages(before, after);
	const titled = (capture: Capture) => Describe.title(capture, text, short);
	const facts = (
		<span className="inline-flex flex-wrap items-center gap-2">
			<Link to={`/snapshots/${encodeURIComponent(before.id)}`} className="text-ink underline">{titled(before)}</Link>
			<span aria-hidden>→</span>
			<ChoiceMenu
				label={text('compare.after')}
				value={after.id}
				options={others.filter(entry => entry.id !== before.id).map(entry => ({ value: entry.id, label: titled(entry) }))}
				onChange={value => setParameters({ a: before.id, b: value })}
			/>
		</span>
	);
	return page(
		<OutageScope missing={missing}>
			<div className="flex flex-col gap-4" data-compare={`${before.id}:${after.id}`}>
				<Section title={text('capture.packages')}>
					{/* Known only with both snapshots: said under the heading drawn while they were read, it moves nothing (D185) */}
					{Comparison.same(before, after) && <Callout tone="info" title={text('compare.same')} />}
					<Changes changes={changes} before={before} after={after} />
				</Section>
			</div>
		</OutageScope>,
		facts
	);
}

/**
 * Each package's change as the family's rows (D162, L1–L2): the change as the mark before its name, said in
 * words only where it changed, since an unchanged package is the comparison's resting state; then its version
 * (before → after when that changed) and its path. The files that changed are the context below the row.
 */
function Changes({ changes, before, after }: { changes: PackageChange[]; before: Capture; after: Capture }) {
	const { text } = usePresence();
	const keys = useRowKeys();
	return (
		<ul className="bui-rows" ref={keys} data-changes>
			{changes.map(change => {
				const words = text(`compare.change.${change.change}`);
				const version = change.change === 'version' ? text('compare.version', { before: change.before!.version, after: change.after!.version }) : (change.after ?? change.before)!.version;
				return (
					<li key={change.path} className="package-entry" data-led="" data-change={change.change} data-path={change.path}>
						<ListRow tag="div" title={change.name} lead={{ state: states[change.change], label: words }} meta={[change.change === 'same' ? null : words, version, <span key="path" className="font-mono">{change.path}</span>]} />
						{(change.change === 'content' || change.change === 'version') && change.before?.stored && change.after?.stored && (
							<div className="package-context bui-row-under">
								<ChangedFiles change={change} before={before} after={after} />
							</div>
						)}
					</li>
				);
			})}
		</ul>
	);
}

/** The files that changed in one package, from the two archives' listings; nobody answering them is the page's line */
function ChangedFiles({ change, before, after }: { change: PackageChange; before: Capture; after: Capture }) {
	const { text, count, captures, size, language } = usePresence();
	const [files, setFiles] = useState<FileChange[] | null>();
	const keys = useRowKeys();
	const [missed, setMissed] = useState<Refusal>();
	const read = async (): Promise<boolean> => {
		try {
			const [earlier, later] = await Promise.all([captures.files(before.id, change.path), captures.files(after.id, change.path)]);
			setFiles(earlier.readable && later.readable ? Comparison.files(earlier.files, later.files) : null);
			setMissed(undefined);
			return true;
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			if (Outage.cause(refusal)) {
				setMissed(refusal);
				return false;
			}
			setMissed(undefined);
			setFiles(null);
			return true;
		}
	};
	useOutage(`changes:${change.path}`, missed ? { title: text('compare.files.show', { name: change.name }), refusal: missed, run: read } : null);
	return (
		<details className="text-small" onToggle={event => (event.currentTarget as HTMLDetailsElement).open && files === undefined && !missed && void read()}>
			<summary className="cursor-pointer">{text('compare.files.show', { name: change.name })}</summary>
			{missed && <Unanswered language={language} frame={false} />}
			{files === null && <p>{text('compare.files.unreadable')}</p>}
			{files && !files.length && <p>{text('compare.files.none')}</p>}
			{files && files.length > 0 && (
				<>
					<ul className="bui-rows mt-2" ref={keys} aria-label={text('compare.files.show', { name: change.name })}>
						{files.map(file => (
							<li key={file.name} data-file-change={file.change}>
								<ListRow tag="div" title={file.name} meta={[text(`compare.file.${file.change}`), size((file.after ?? file.before)!.size)]} />
							</li>
						))}
					</ul>
					<EndLine text={count('compare.files.count', files.length)} />
				</>
			)}
			{files === undefined && !missed && <Waiting label={Loading.text({ name: text('loading.files'), language })} language={language} kind="lines" rows={2} frame={false} />}
		</details>
	);
}
